import { ReactElement } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import TextField, { BaseTextFieldProps } from '@mui/material/TextField';
import { isStringNullishOrWhitespace } from 'common/string';
import { Control, Controller, Path, ControllerFieldState } from 'react-hook-form';
import i18n from 'i18next';

interface MaxLengthTextFieldHelperProps {
  currentLength: number;
  maxLength: number;
  fieldState: ControllerFieldState;
  required?: boolean;
}

function MaxLengthTextFieldHelper({ currentLength, maxLength, fieldState, required }: MaxLengthTextFieldHelperProps) {
  const theme = useTheme();
  const requiredHelperText = i18n.t('common.required-helper-text');
  const requiredError = i18n.t('common.required-error-text');

  let helperText: string | undefined = undefined;
  if (fieldState.invalid) {
    if (fieldState.error?.type === 'required') {
      helperText = requiredError;
    } else if (fieldState.error?.type === 'isDuplicate') {
      helperText = i18n.t('project-details-page.calculation-dialog.name-must-be-unique');
    }
  }

  if (!fieldState.invalid && required) {
    helperText = requiredHelperText;
  }

  if (isStringNullishOrWhitespace(helperText)) {
    <Stack direction="row-reverse" justifyContent="space-between" component="span">
      <Box component="span">
        {currentLength}/{maxLength}
      </Box>
    </Stack>;
  }
  return (
    <Stack direction="row" justifyContent="space-between" component="span">
      <Box component="span">{helperText}</Box>
      <Box component="span" sx={{ color: theme.palette.text.secondary }}>
        {currentLength}/{maxLength}
      </Box>
    </Stack>
  );
}

export function requireNonWhitespace<TFieldValue>(value: TFieldValue): boolean {
  return !!(value as unknown as string)?.trim();
}

export type MaxLengthTextFieldProps<TFieldValues> = BaseTextFieldProps &
  Omit<BaseTextFieldProps, 'value'> & {
    control?: Control<TFieldValues>;
    maxLength: number;
    name: Path<TFieldValues>;
    required?: boolean;
    validateCalculationName?: (newName: string) => boolean;
  };

export function MaxLengthTextField<TFieldValues>({
  control,
  maxLength,
  name,
  required,
  validateCalculationName,
  ...textFieldProps
}: MaxLengthTextFieldProps<TFieldValues>): ReactElement {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        maxLength,
        required,
        validate: {
          isDuplicate: (v) => validateCalculationName && validateCalculationName(`${v}`),
        },
      }}
      render={({ field, fieldState }) => {
        const currentLength = (field.value as string)?.length || 0;
        return (
          <TextField
            error={fieldState.invalid}
            onChange={field.onChange}
            value={field.value}
            inputProps={{
              maxLength: maxLength,
            }}
            helperText={
              <MaxLengthTextFieldHelper currentLength={currentLength} maxLength={maxLength} fieldState={fieldState} required={required} />
            }
            {...textFieldProps}
          />
        );
      }}
    />
  );
}
